import { apiSimulator } from './api-simulator.service';
import {
  SponsorMarketOffer,
  SponsorContract,
  SponsorContractsResponse,
  SponsorType,
  SponsorContractStatus,
} from '../models/types';

/**
 * Normaliza y mapea una oferta de patrocinio desde el backend.
 * Soporta tanto el formato oficial del backend ({ offer: {...}, quote: {...}, sponsorName, ... })
 * como formatos planos o mocks.
 */
export function mapBackendSponsorOffer(rawItem: any): SponsorMarketOffer {
  if (!rawItem) return {} as SponsorMarketOffer;

  const offerObj = rawItem.offer || {};
  const quoteObj = rawItem.quote || {};

  // El id de la oferta a contratar es el id del contrato u oferta interna del microservicio
  const id = String(
    offerObj.id ||
    rawItem.id ||
    rawItem.offerId ||
    `offer-${Date.now()}-${Math.random()}`
  );

  const sponsorId =
    offerObj.sponsorId ||
    rawItem.sponsorId ||
    undefined;

  const sponsorName =
    rawItem.sponsorName ||
    offerObj.sponsorName ||
    rawItem.name ||
    'Patrocinador Oficial';

  const logoUrl =
    rawItem.logoUrl ||
    offerObj.logoUrl ||
    null;

  const websiteUrl =
    rawItem.websiteUrl ||
    offerObj.websiteUrl ||
    null;

  // En el backend viene "category": "BILLBOARD" | "SHIRT" | "STADIUM" dentro de offer
  const rawType = String(
    offerObj.category ||
    rawItem.category ||
    offerObj.type ||
    rawItem.type ||
    ''
  ).toUpperCase();

  let type: SponsorType = 'BILLBOARD';
  if (rawType.includes('SHIRT') || rawType.includes('CAMISETA')) {
    type = 'SHIRT';
  } else if (rawType.includes('STADIUM') || rawType.includes('ESTADIO')) {
    type = 'STADIUM';
  } else {
    type = 'BILLBOARD';
  }

  // Duración efectiva en jornadas
  const durationMatchdays = Number(
    quoteObj.standardDuration ??
    offerObj.duration ??
    quoteObj.durationMatchdays ??
    offerObj.durationMatchdays ??
    rawItem.durationMatchdays ??
    rawItem.duration ??
    1
  );

  // Importe fijo personalizado devuelto por el backend (finalFixed en quote)
  const fixedAmount = Number(
    quoteObj.finalFixed ??
    quoteObj.fixedAmount ??
    offerObj.baseFixed ??
    offerObj.fixedAmount ??
    rawItem.fixedAmount ??
    0
  );

  // Bonus por victoria personalizado (finalWinBonus en quote)
  const hasWinBonus =
    quoteObj.finalWinBonus !== undefined && quoteObj.finalWinBonus !== null
      ? quoteObj.finalWinBonus
      : quoteObj.winBonusAmount !== undefined && quoteObj.winBonusAmount !== null
        ? quoteObj.winBonusAmount
        : offerObj.baseWinBonus !== undefined && offerObj.baseWinBonus !== null
          ? offerObj.baseWinBonus
          : rawItem.winBonusAmount !== undefined && rawItem.winBonusAmount !== null
            ? rawItem.winBonusAmount
            : null;

  const winBonusAmount = hasWinBonus !== null ? Number(hasWinBonus) : null;

  const blockedReason =
    rawItem.blockedReason ||
    offerObj.blockedReason ||
    null;

  return {
    id,
    sponsorId,
    sponsorName,
    logoUrl,
    websiteUrl,
    type,
    durationMatchdays,
    quote: {
      fixedAmount,
      winBonusAmount,
      durationMatchdays,
    },
    fixedAmount,
    winBonusAmount,
    blockedReason,
    status: offerObj.status || rawItem.status || 'ACTIVE',
    matchdayId: offerObj.matchdayId || rawItem.matchdayId,
    rawOffer: offerObj,
    rawQuote: quoteObj,
  };
}

/**
 * Normaliza y mapea un contrato de patrocinio desde el backend.
 */
export function mapBackendSponsorContract(rawItem: any, fallbackClubId: string = ''): SponsorContract {
  if (!rawItem) return {} as SponsorContract;

  const contractObj = rawItem.contract || rawItem;
  const termsObj = rawItem.terms || rawItem.quote || contractObj.terms || contractObj.quote || {};

  const id = String(
    contractObj.id ||
    contractObj.contractId ||
    rawItem.id ||
    `contract-${Date.now()}`
  );

  const clubId = String(
    contractObj.clubId ||
    rawItem.clubId ||
    fallbackClubId
  );

  const offerId =
    contractObj.offerId ||
    rawItem.offerId ||
    undefined;

  const sponsorName =
    rawItem.sponsorName ||
    contractObj.sponsorName ||
    rawItem.sponsor?.name ||
    contractObj.sponsor?.name ||
    rawItem.offer?.sponsorName ||
    rawItem.offer?.sponsor?.name ||
    'Patrocinador Oficial';

  const logoUrl =
    rawItem.logoUrl ||
    contractObj.logoUrl ||
    rawItem.sponsor?.logoUrl ||
    contractObj.sponsor?.logoUrl ||
    rawItem.offer?.logoUrl ||
    rawItem.offer?.sponsor?.logoUrl ||
    null;

  const rawType = String(
    contractObj.category ||
    rawItem.category ||
    contractObj.type ||
    rawItem.type ||
    rawItem.offer?.category ||
    contractObj.offer?.category ||
    rawItem.offer?.type ||
    ''
  ).toUpperCase();

  let type: SponsorType = 'BILLBOARD';
  if (rawType.includes('SHIRT') || rawType.includes('CAMISETA')) {
    type = 'SHIRT';
  } else if (rawType.includes('STADIUM') || rawType.includes('ESTADIO')) {
    type = 'STADIUM';
  } else {
    type = 'BILLBOARD';
  }

  const rawStatus = String(
    contractObj.status ||
    rawItem.status ||
    'ACTIVE'
  ).toUpperCase();

  let status: SponsorContractStatus = 'ACTIVE';
  if (rawStatus === 'PENDING_ACTIVATION' || rawStatus.includes('PENDING')) {
    status = 'PENDING_ACTIVATION';
  } else if (rawStatus === 'EXPIRED' || rawStatus.includes('EXPIRED')) {
    status = 'EXPIRED';
  } else if (rawStatus === 'CANCELLED' || rawStatus.includes('CANCEL')) {
    status = 'CANCELLED';
  } else {
    status = 'ACTIVE';
  }

  const economicModality =
    contractObj.economicModality ||
    contractObj.mode ||
    rawItem.economicModality ||
    rawItem.mode ||
    'FIXED_PLUS_WIN';

  const fixedAmount = Number(
    termsObj.finalFixed ??
    termsObj.fixedAmount ??
    contractObj.baseFixed ??
    contractObj.fixedAmount ??
    rawItem.fixedAmount ??
    0
  );

  const hasWinBonus =
    termsObj.finalWinBonus !== undefined && termsObj.finalWinBonus !== null
      ? termsObj.finalWinBonus
      : termsObj.winBonusAmount !== undefined && termsObj.winBonusAmount !== null
        ? termsObj.winBonusAmount
        : contractObj.baseWinBonus !== undefined && contractObj.baseWinBonus !== null
          ? contractObj.baseWinBonus
          : rawItem.winBonusAmount !== undefined && rawItem.winBonusAmount !== null
            ? rawItem.winBonusAmount
            : null;

  const winBonusAmount = hasWinBonus !== null ? Number(hasWinBonus) : null;

  const startMatchdayNumber = Number(
    contractObj.startMatchdayNumber ??
    contractObj.startRound ??
    contractObj.startMatchday ??
    rawItem.startMatchdayNumber ??
    rawItem.startRound ??
    1
  );

  const endMatchdayNumber = Number(
    contractObj.endMatchdayNumber ??
    contractObj.endRound ??
    contractObj.endMatchday ??
    rawItem.endMatchdayNumber ??
    rawItem.endRound ??
    (startMatchdayNumber + 3)
  );

  const remainingMatchdays = Number(
    contractObj.remainingMatchdays ??
    contractObj.remainingRounds ??
    contractObj.remaining ??
    rawItem.remainingMatchdays ??
    rawItem.remainingRounds ??
    Math.max(0, endMatchdayNumber - startMatchdayNumber + 1)
  );

  const accumulatedRevenue = Number(
    contractObj.accumulatedRevenue ??
    contractObj.revenue ??
    contractObj.totalRevenue ??
    rawItem.accumulatedRevenue ??
    rawItem.revenue ??
    (status === 'ACTIVE' ? fixedAmount : 0)
  );

  return {
    id,
    clubId,
    offerId,
    sponsorName,
    logoUrl,
    type,
    status,
    economicModality,
    terms: {
      fixedAmount,
      winBonusAmount,
      durationMatchdays: endMatchdayNumber - startMatchdayNumber + 1,
    },
    fixedAmount,
    winBonusAmount,
    startMatchdayNumber,
    endMatchdayNumber,
    remainingMatchdays,
    accumulatedRevenue,
    signedAt: contractObj.signedAt || rawItem.signedAt,
    effectiveAt: contractObj.effectiveAt || rawItem.effectiveAt,
  };
}

export const sponsorsApi = {
  /**
   * GET /api/clubs/{clubId}/economy/sponsors/market
   * Consultar ofertas vigentes de patrocinio para la jornada en curso.
   * El backend determina la jornada.
   */
  async getMarketOffers(clubId: string): Promise<SponsorMarketOffer[]> {
    const raw = await apiSimulator.callWithMockFallback<any>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/economy/sponsors/market`,
      service: 'stadium-economy',
      mockName: 'sponsors-market',
    });

    let rawList: any[] = [];
    if (Array.isArray(raw)) {
      rawList = raw;
    } else if (raw && Array.isArray(raw.offers)) {
      rawList = raw.offers;
    } else if (raw && Array.isArray(raw.items)) {
      rawList = raw.items;
    } else if (raw && Array.isArray(raw.content)) {
      rawList = raw.content;
    }

    return rawList.map(mapBackendSponsorOffer);
  },

  /**
   * GET /api/clubs/{clubId}/economy/sponsors/contracts?page={page}&size={size}
   * Consultar contratos de patrocinio del club (paginado).
   */
  async getContracts(
    clubId: string,
    page: number = 0,
    size: number = 20
  ): Promise<SponsorContractsResponse> {
    const raw = await apiSimulator.callWithMockFallback<any>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/economy/sponsors/contracts?page=${page}&size=${size}`,
      service: 'stadium-economy',
      mockName: 'sponsors-contracts',
    });

    let rawList: any[] = [];
    let totalElements = 0;
    let totalPages = 1;

    if (Array.isArray(raw)) {
      rawList = raw;
      totalElements = raw.length;
      totalPages = 1;
    } else {
      rawList = raw?.content || raw?.items || raw?.contracts || [];
      totalElements = raw?.totalElements ?? rawList.length;
      totalPages = raw?.totalPages ?? 1;
    }

    const mappedItems: SponsorContract[] = rawList.map((c: any) =>
      mapBackendSponsorContract(c, clubId)
    );

    return {
      items: mappedItems,
      content: mappedItems,
      page: raw?.page ?? page,
      size: raw?.size ?? size,
      totalElements,
      totalPages,
    };
  },

  /**
   * Obtiene todos los contratos del club recorriendo todas las páginas disponibles
   * para asegurar que ningún contrato activo o pendiente de activación quede omitido.
   */
  async getAllContracts(clubId: string): Promise<SponsorContract[]> {
    const firstPage = await this.getContracts(clubId, 0, 20);
    const allItems: SponsorContract[] = [...(firstPage.items || [])];

    const totalPages = firstPage.totalPages || 1;
    if (totalPages > 1) {
      const remainingPagePromises: Promise<SponsorContractsResponse>[] = [];
      for (let p = 1; p < totalPages; p++) {
        remainingPagePromises.push(this.getContracts(clubId, p, 20));
      }
      const remainingPages = await Promise.all(remainingPagePromises);
      for (const pageRes of remainingPages) {
        if (pageRes.items && pageRes.items.length > 0) {
          allItems.push(...pageRes.items);
        }
      }
    }

    return allItems;
  },

  /**
   * POST /api/clubs/{clubId}/economy/sponsors/contracts
   * Contratar una oferta de patrocinio.
   * Envía X-Request-Id y {"offerId": "<UUID>"}
   */
  async contractOffer(
    clubId: string,
    offerId: string,
    requestId: string
  ): Promise<SponsorContract> {
    const raw = await apiSimulator.callWithMockFallback<any>({
      method: 'POST',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/economy/sponsors/contracts`,
      headers: {
        'X-Request-Id': requestId,
      },
      body: {
        offerId,
      },
      service: 'stadium-economy',
      mockName: 'contract-sponsor',
      transformMock: (mock) => ({
        ...mock,
        id: `c-${Date.now()}`,
        clubId,
        offerId,
      }),
    });

    return mapBackendSponsorContract(raw, clubId);
  },
};
